"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import Image from "next/image"
import Link from "next/link"
import { Globe, ArrowRight, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { AuthButton } from "@/components/auth-button"
import { cn } from "@/lib/utils"

const navigation = [
  { name: "Home", href: "/" },
  { name: "Events", href: "/events" },
  { name: "About", href: "/about" },
  { name: "Articles", href: "/articles" },
  { name: "Contact", href: "/contact" },
]

const markets = [
  { name: "UAE", href: "/markets/uae" },
  { name: "KSA", href: "/markets/ksa" },
  { name: "India", href: "/markets/india" },
  { name: "China", href: "/markets/china" },
  { name: "Europe", href: "/markets/europe" },
  { name: "North America", href: "/markets/north-america" },
]

export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [dbNavigation, setDbNavigation] = useState<Array<{ name: string; href: string }>>([])

  // Load the "header" menu location managed from /admin/menus. Falls back
  // to the hardcoded navigation whenever nothing is configured or the
  // request fails.
  useEffect(() => {
    fetch("/api/public/menus", { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : []))
      .then((menus: Array<{ location?: string; isActive?: boolean; items?: Array<{ title: string; url: string; target?: string; order?: number }> }>) => {
        const headerMenu = menus.find((m) => m.location === "header" && m.isActive)
        if (headerMenu?.items?.length) {
          const items = [...headerMenu.items]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((item) => ({ name: item.title, href: item.url }))
          setDbNavigation(items)
        }
      })
      .catch(() => {})
  }, [])

  const navItems = dbNavigation.length > 0 ? dbNavigation : navigation

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false)
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4 pointer-events-none">
        <header
          className={cn(
            "pointer-events-auto transition-[background,padding,max-width] duration-500 ease-in-out flex items-center justify-between",
            "rounded-full border border-white/20 shadow-2xl",
            scrolled
              ? "bg-background/60 backdrop-blur-xl px-6 py-2 w-full max-w-6xl"
              : "bg-background/30 backdrop-blur-md px-8 py-3 w-full max-w-7xl"
          )}
        >
          {/* Logo Section */}
          <Link
            href="/"
            className="flex items-center gap-3 group transition-transform active:scale-95"
          >
            <div className="relative w-8 h-8 transition-transform">
              <Image
                src="/vantage-crest-icon.png"
                alt="Vantage Crest"
                width={32}
                height={32}
                className="w-full h-full object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className="px-4 py-2 text-sm font-medium text-foreground/70 hover:text-primary transition-all rounded-full hover:bg-white/10"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-3">
              <AuthButton />
            </div>

            <Link href="/events" className="hidden sm:block">
              <Button
                size="sm"
                className="rounded-full px-5 py-1.5 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all active:scale-95 shadow-lg shadow-primary/20"
              >
                View Events
              </Button>
            </Link>

            {/* Mobile Menu Trigger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open mobile navigation menu"
              className="lg:hidden relative w-10 h-10 rounded-full flex flex-col items-center justify-center gap-1.5 border border-white/20 bg-white/5 hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--home-gold,#c4a056)]"
            >
              <span className="w-5 h-[2px] bg-[var(--home-cream,#f6f1e6)] rounded-full transition-transform duration-300" />
              <span className="w-3.5 h-[2px] bg-[var(--home-gold,#c4a056)] rounded-full transition-transform duration-300 self-center" />
            </button>
          </div>
        </header>
      </div>

      {/* Full-Screen Mobile Navigation Overlay */}
      {mounted &&
        mobileMenuOpen &&
        createPortal(
          <div
            className="mobile-nav-overlay fixed inset-0 z-[100] h-[100dvh] w-screen overflow-y-auto bg-[var(--home-navy-deep,#001c3a)]/98 backdrop-blur-2xl text-[var(--home-cream,#f6f1e6)] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation Menu"
          >
            {/* Ambient luxury lighting */}
            <div
              className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-50 z-0"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in oklch, var(--home-gold, #c4a056) 24%, transparent) 0%, transparent 70%)",
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-30 z-0"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in oklch, var(--home-navy, #002d5c) 90%, #004b99) 0%, transparent 70%)",
              }}
              aria-hidden
            />

            {/* Inner Content Container */}
            <div className="relative z-10 w-full max-w-lg mx-auto flex flex-col justify-between min-h-full px-6 py-6 sm:px-8 sm:py-8">
              {/* Top header bar */}
              <div className="flex items-center justify-between pb-6 border-b border-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_12%,transparent)]">
                <Link
                  href="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 group"
                >
                  <div className="relative w-8 h-8 rounded-sm overflow-hidden flex-shrink-0 bg-white/5 p-1 border border-[color-mix(in_oklch,var(--home-gold,#c4a056)_30%,transparent)]">
                    <Image
                      src="/vantage-crest-icon.png"
                      alt="The Vantage Crest"
                      width={32}
                      height={32}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <span className="font-heading font-medium text-lg tracking-tight text-[var(--home-cream,#f6f1e6)]">
                    The Vantage Crest
                  </span>
                </Link>

                {/* Circular luxury close button */}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close navigation menu"
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-[color-mix(in_oklch,var(--home-gold,#c4a056)_35%,transparent)] bg-white/5 text-[var(--home-cream,#f6f1e6)] hover:bg-[var(--home-gold,#c4a056)] hover:text-[var(--home-navy-deep,#001c3a)] hover:border-[var(--home-gold,#c4a056)] transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-[var(--home-gold,#c4a056)]"
                >
                  <X className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90" />
                </button>
              </div>

              {/* Navigation Links with Staggered Cascade Animation */}
              <div className="py-8 flex flex-col justify-center flex-grow">
                <p className="mobile-nav-item text-[0.68rem] uppercase font-mono tracking-[0.25em] text-[var(--home-gold,#c4a056)] opacity-80 mb-4">
                  Directory
                </p>
                <nav className="flex flex-col gap-1">
                  {navItems.map((item, idx) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="mobile-nav-item group flex items-baseline justify-between py-3.5 border-b border-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_8%,transparent)] transition-all duration-200"
                      style={{ animationDelay: `${0.08 + idx * 0.05}s` }}
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="font-mono text-xs font-semibold tracking-widest text-[var(--home-gold,#c4a056)] opacity-60 group-hover:opacity-100 transition-opacity">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[var(--home-cream,#f6f1e6)] group-hover:text-[var(--home-gold,#c4a056)] group-hover:translate-x-2 transition-all duration-200">
                          {item.name}
                        </span>
                      </div>
                      <ArrowRight className="h-5 w-5 text-[var(--home-gold,#c4a056)] opacity-0 -translate-x-3 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
